import { ImageInspector } from '@sohanscript/ui/image-inspector';
import type * as React from 'react';
import { useState } from 'react';

export type RenderImageProps = Omit<
	React.ComponentPropsWithoutRef<'img'>,
	'src'
> & {
	src: string;
};

export type RenderImage = (props: RenderImageProps) => React.ReactNode;

// NOTE: icons, badges and logos are decoration. They read fine inline and must
// never earn a window, so only real content images become click-to-inspect.
const MIN_DECLARED_EDGE = 320;
const MIN_NATURAL_EDGE = 400;
const MIN_RENDERED_EDGE = 120;

type MarkdownImageProps = RenderImageProps & {
	/** Host image component (lazy loading, CDN resizing, placeholder). */
	renderImage?: RenderImage;
	/** Set false when the image sits inside a link; triggers cannot nest in an anchor. */
	inspect?: boolean;
};

function numericEdge(value: string | number | undefined): number | undefined {
	const edge = typeof value === 'number' ? value : Number(value);
	return Number.isFinite(edge) && edge > 0 ? edge : undefined;
}

function isIconSource(src: string): boolean {
	return /\.svg(?:[?#]|$)/i.test(src);
}

function isInspectable({ src, width, height }: RenderImageProps): boolean {
	if (isIconSource(src)) return false;

	const declared = Math.max(numericEdge(width) ?? 0, numericEdge(height) ?? 0);
	// NOTE: no declared size means the markup trusts the intrinsic size, so the
	// measurement after load has the final say.
	return declared === 0 || declared >= MIN_DECLARED_EDGE;
}

function filenameOf(src: string): string | undefined {
	const name = src.split(/[?#]/)[0]?.split('/').pop();
	if (!name) return undefined;
	try {
		return decodeURIComponent(name);
	} catch {
		return name;
	}
}

/** Markdown image that opens the inspector — unless it is too small to inspect. */
export function MarkdownImage({
	renderImage,
	inspect = true,
	...props
}: MarkdownImageProps) {
	const [isSmall, setIsSmall] = useState(false);

	const handleLoad: React.ReactEventHandler<HTMLImageElement> = (event) => {
		props.onLoad?.(event);

		const image = event.currentTarget;
		const { width, height } = image.getBoundingClientRect();
		const naturalEdge = Math.max(image.naturalWidth, image.naturalHeight);
		const renderedEdge = Math.max(width, height);

		// NOTE: the static filter cannot know intrinsic size, so an image that
		// turns out small once loaded drops back to a plain <img> instead of
		// keeping a trigger that would only ever zoom a thumbnail.
		setIsSmall(
			naturalEdge < MIN_NATURAL_EDGE || renderedEdge < MIN_RENDERED_EDGE,
		);
	};

	const imageProps: RenderImageProps = { ...props, onLoad: handleLoad };
	const image = renderImage ? (
		renderImage(imageProps)
	) : (
		<img {...imageProps} alt={props.alt ?? ''} />
	);

	if (!(inspect && !isSmall && isInspectable(props))) return <>{image}</>;

	return (
		<ImageInspector
			src={props.src}
			filename={filenameOf(props.src)}
			width={numericEdge(props.width)}
			height={numericEdge(props.height)}
			// NOTE: a full-width block keeps the image's own classes (`w-full`,
			// `mx-auto`, …) resolving against the prose width, exactly as before.
			className="block w-full"
		>
			{image}
		</ImageInspector>
	);
}
